import type { SettingHistory, SettingView } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { settingHistoryQuery } from '../api/settings';
import { actorWords, HistoryDrawer, HistoryLine, type HistoryEntry } from '../ui';

/** A setting's values over time, oldest first, each with what it was before. */
export function settingEntries(
  history: SettingHistory | undefined,
  words: (value: unknown) => string,
): HistoryEntry[] {
  const oldestFirst = [...(history?.entries ?? [])].sort((a, b) =>
    a.recordedAt.localeCompare(b.recordedAt),
  );
  return oldestFirst.map((e, i) => ({
    id: e.id,
    at: e.recordedAt,
    actor: { name: actorWords(e.by?.name ?? 'MyRewards setup', e.previewedBy?.name) },
    before: i === 0 ? null : words(oldestFirst[i - 1]?.value),
    after: words(e.value),
    reason: e.reason,
    source: 'screen',
  }));
}

/**
 * "Last changed by Meredith Okonkwo on Oct 2, 2026 · History" under a setting the Stock Admin
 * screens follow (PRD §17), with its History drawer. The setting is changed in Settings.
 */
export function SettingChange({
  view,
  title,
  subject,
  words,
}: {
  view: SettingView;
  title: string;
  subject: string;
  words: (value: unknown) => string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const history = useQuery({ ...settingHistoryQuery(view.key, view.scope), enabled: open });
  return (
    <>
      <HistoryLine
        lastChange={
          view.lastChanged === null
            ? null
            : { by: view.lastChanged.by?.name ?? 'MyRewards setup', on: view.lastChanged.at }
        }
        onOpenHistory={() => {
          setOpen(true);
        }}
      />
      <HistoryDrawer
        isOpen={open}
        onOpenChange={setOpen}
        subject={subject}
        title={title}
        current={{ label: 'Now', value: words(view.value) }}
        entries={settingEntries(history.data, words)}
      />
    </>
  );
}
